import {
  Application,
  Merchant,
  MerchantSchema,
  PayeeDetailsPerCurrency,
} from "@/types/merchant";
import { queryOptions } from "@tanstack/react-query";
import { env } from "@/env";
import { parseVolumeJwt, redirectToSignIn } from "./auth";

import { MerchantAccessError, shouldRetryQuery } from "./query-retry";

export function getMerchantQuery({
  accessToken,
  merchantId,
}: {
  accessToken: string;
  merchantId: string;
}) {
  const session = parseVolumeJwt(accessToken);
  const merchantQuery = queryOptions({
    queryKey: ["merchant", merchantId, session.userAccountId, session.jti],
    staleTime: 30_000,
    gcTime: 5 * 60_000,
    retry: shouldRetryQuery,
    queryFn: async ({ signal }) => {
      const response = await fetch(
        `${env.VITE_VOLUME_URL}/api/merchants/${merchantId}`,
        {
          signal,
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );

      if (response.status === 401) {
        throw redirectToSignIn(false);
      }

      if (response.status === 403) throw new MerchantAccessError();

      if (!response.ok) {
        throw new Error("Failed to fetch merchant details");
      }
      const json = await response.json();
      const merchantData = MerchantSchema.parse(json);

      return getMerchantQueryData(merchantData);
    },
  });
  return merchantQuery;
}

export interface MerchantQuery {
  merchant: Merchant;
  applications: ({
    hasEUR: boolean;
    hasGBP: boolean;
    hasEURForDefaultVirtual: boolean;
    hasGBPForDefaultVirtual: boolean;
    shouldSendEmailForExternalPayinPayment: boolean;
    shouldSendWebhookForExternalPayinPayment: boolean;
    vaProviders: PayeeDetailsPerCurrency["vaProviderType"][];
  } & Application)[];
}

export function getMerchantQueryData(merchantData: Merchant): MerchantQuery {
  const processedApplications = merchantData.applications.map((application) => {
    const payeeDetails = application.payeeDetailsPerCurrency || [];
    const hasEUR = payeeDetails.some((c) => c.forCurrency === "EUR");
    const hasGBP = payeeDetails.some((c) => c.forCurrency === "GBP");

    return {
      hasEUR,
      hasGBP,
      hasEURForDefaultVirtual: payeeDetails.some(
        (c) =>
          c.forCurrency === "EUR" && c.defaultForCurrency && c.isVirtualAccount,
      ),
      hasGBPForDefaultVirtual: payeeDetails.some(
        (c) =>
          c.forCurrency === "GBP" && c.defaultForCurrency && c.isVirtualAccount,
      ),
      shouldSendEmailForExternalPayinPayment:
        application.sendEmailForExternalPayinPayment ?? false,
      shouldSendWebhookForExternalPayinPayment:
        application.sendWebhookForExternalPayinPayment ?? false,
      vaProviders:
        application.payeeDetailsPerCurrency?.map((p) => p.vaProviderType) ?? [],
      ...application,
    };
  });

  return {
    merchant: merchantData,
    applications: processedApplications,
  };
}
